import { ArrowLeft } from 'lucide-react';
import { useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';

interface BackButtonProps {
  onClick: () => void;
  label?: string;
  srLabel?: string;
  className?: string;
  /** Use light variant for dark backgrounds (e.g., Laylatul Qadr guide) */
  variant?: 'default' | 'light';
}

export const BackButton = ({
  onClick,
  label,
  srLabel = 'Go back',
  className,
  variant = 'default',
}: BackButtonProps) => {
  // ESC key shortcut
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClick();
      }
    },
    [onClick]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <button
      onClick={onClick}
      aria-label={srLabel}
      className={cn(
        // Base: 44px tap target, consistent positioning
        'inline-flex items-center gap-2 h-11 min-w-[44px] rounded-md',
        'transition-all duration-200 ease-out',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'hover:-translate-x-0.5 hover:opacity-70',
        // Variant styles
        variant === 'default'
          ? 'text-muted-foreground hover:text-foreground'
          : 'text-white/60 hover:text-white',
        // With label: add padding; icon-only: square
        label ? 'px-2' : 'justify-center w-11',
        className
      )}
    >
      <ArrowLeft className="h-5 w-5 flex-shrink-0" />
      {label && (
        <span className="text-sm tracking-wide">{label}</span>
      )}
      {!label && <span className="sr-only">{srLabel}</span>}
    </button>
  );
};
